import React, { useCallback, useEffect, useState } from "react";
import {
  CalendarClock, CheckCircle2, Clock, Loader2, MapPin, Pencil, Plus, Star, Trash2, Users2, XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fdate, fdatetime, money } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field } from "@/pages/crm/LeadForm";
import { DemoExpenses } from "./DemoExpenses";
import { shift, spanDays } from "./DemoForm";
import { cn } from "@/lib/utils";

export const TONE = {
  Proposed: "info", Confirmed: "success", Rescheduled: "warning",
  Completed: "primary", "Feedback Received": "success", Cancelled: "danger",
};

const Row = ({ label, value }) => (
  <div className="flex items-start justify-between gap-4 border-b border-border/60 py-2 last:border-0">
    <span className="text-xs uppercase tracking-wider text-muted-foreground">{label}</span>
    <span className="max-w-[62%] text-right text-sm font-medium">{value || "\u2014"}</span>
  </div>
);

const ProductPicker = ({ label, testId, options, picked, onChange }) => (
  <Field label={label}>
    <div className="space-y-2" data-testid={testId}>
      <div className="flex flex-wrap gap-2">
        {picked.map((p) => (
          <span key={p.product_id || p.name} className="flex items-center gap-1.5 rounded-full border border-primary/40 bg-primary-soft px-2.5 py-1 text-xs font-medium">
            {p.name}
            <button type="button" data-testid={`${testId}-remove-${p.name}`}
              onClick={() => onChange(picked.filter((x) => x.name !== p.name))}>
              <XCircle className="h-3 w-3" />
            </button>
          </span>
        ))}
        {picked.length === 0 && <span className="text-xs text-muted-foreground">Nothing selected.</span>}
      </div>
      <Select value="" onValueChange={(id) => {
        const p = options.find((x) => x.product_id === id);
        if (p && !picked.some((x) => x.name === p.name)) onChange([...picked, p]);
      }}>
        <SelectTrigger data-testid={`${testId}-add`}><SelectValue placeholder="Add a product" /></SelectTrigger>
        <SelectContent>
          {options.filter((p) => !picked.some((x) => x.name === p.name))
            .map((p) => <SelectItem key={p.product_id} value={p.product_id}>{p.name}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  </Field>
);

const Stars = ({ value }) => (
  <span className="inline-flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} className={cn("h-4 w-4", i <= value ? "fill-primary text-primary" : "text-muted-foreground/40")} />
    ))}
  </span>
);

export const DemoDetail = ({ demoId, open, onOpenChange, onChanged }) => {
  const { can, isAdmin } = useAuth();
  const [demo, setDemo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("");     // confirm | reschedule | cancel
  const [form, setForm] = useState({});
  const [comp, setComp] = useState({
    attendees: [{ name: "", designation: "", email: "" }], actual_days: 1,
    travel_start: "", travel_end: "", products_demonstrated: [],
    summary: "", performance: "Good", outcome: "Follow-up required", request_feedback: true,
  });
  const [adminForm, setAdminForm] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [staff, setStaff] = useState([]);
  const [travelEdited, setTravelEdited] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (!catalog.length) api.get("/products").then((r) => setCatalog(r.data)).catch(() => {});
    if (!staff.length) api.get("/staff/assignable").then((r) => setStaff(r.data)).catch(() => {});
  }, [open]); // eslint-disable-line

  const load = useCallback(async (silent = false) => {
    if (!demoId) return;
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get(`/demos/${demoId}`);
      setDemo(data);
      setComp((c) => ({
        ...c, actual_days: data.actual_days || data.days || 1,
        travel_start: data.travel_start || "", travel_end: data.travel_end || "",
        products_demonstrated: data.products_demonstrated?.length ? data.products_demonstrated : (data.products || []),
      }));
    } catch (e) {
      toast.error(errMsg(e, "Could not open this record."));
      onOpenChange(false);
    } finally { setLoading(false); }
  }, [demoId]); // eslint-disable-line

  useEffect(() => { if (open) load(); }, [open, demoId]); // eslint-disable-line

  const saveAdmin = async () => {
    setBusy(true);
    try {
      await api.patch(`/demos/${demo.id}/admin`, {
        ...adminForm, days: Number(adminForm.days || 1), actual_days: Number(adminForm.actual_days || 0),
        attendees: (adminForm.attendees || []).filter((a) => a.name?.trim()),
      });
      toast.success("Record corrected \u00b7 the change is on the timeline");
      setAdminForm(null);
      await load(true);
      onChanged?.();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const openAction = (m) => {
    const start = demo.start_date || demo.proposed_start;
    const end = demo.end_date || demo.proposed_end || start;
    const onsite = demo.mode !== "Online" && demo.mode !== "At our office";
    setTravelEdited(false);
    setForm({
      start_date: start, end_date: end,
      days: demo.days || (start && end ? spanDays(start, end) : 1),
      travel_start: demo.travel_start || (onsite ? start : ""),
      travel_end: demo.travel_end || (onsite ? end : ""),
      note: "", reason: "", remarks: "", notify_customer: true,
    });
    setMode(m);
  };

  const syncTravel = (f) => {
    if (travelEdited) return f;
    const onsite = demo.mode !== "Online" && demo.mode !== "At our office";
    return onsite
      ? { ...f, travel_start: f.start_date || "", travel_end: f.end_date || f.start_date || "" }
      : { ...f, travel_start: "", travel_end: "" };
  };
  const setActStart = (v) => setForm((f) => {
    const end = f.end_date && f.end_date >= v ? f.end_date : v;
    return syncTravel({ ...f, start_date: v, end_date: end, days: spanDays(v, end) });
  });
  const setActEnd = (v) => setForm((f) => syncTravel({
    ...f, end_date: v,
    days: v && f.start_date && v >= f.start_date ? spanDays(f.start_date, v) : f.days,
  }));
  const setActDays = (v) => setForm((f) => {
    const n = Number(v);
    const end = n >= 1 && f.start_date ? shift(f.start_date, n - 1) : f.end_date;
    return syncTravel({ ...f, days: v, end_date: end });
  });

  const runAction = async () => {
    setBusy(true);
    try {
      const path = mode === "cancel" ? "cancel" : mode;
      const { data } = await api.post(`/demos/${demo.id}/${path}`, form);
      toast.success({
        confirm: "Dates confirmed and the customer has been emailed",
        reschedule: "Rescheduled \u00b7 customer and directors informed",
        cancel: "Cancelled",
      }[mode]);
      if (data?.email?.status === "outbox") {
        toast.info("The email is waiting in the Email Outbox until SMTP is configured.");
      }
      setMode("");
      await load(true);
      onChanged?.();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const complete = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/demos/${demo.id}/complete`, {
        ...comp, attendees: comp.attendees.filter((a) => a.name.trim()),
        actual_days: Number(comp.actual_days || 1),
      });
      toast.success("Session closed \u00b7 feedback request sent to the customer");
      if (data?.email?.status === "outbox") {
        toast.info("The feedback email is waiting in the Email Outbox until SMTP is configured.");
      }
      setMode("");
      await load(true);
      onChanged?.();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const editable = can("demos", "edit");
  const isOpenStatus = demo && ["Proposed", "Confirmed", "Rescheduled"].includes(demo.status);
  const allProducts = [
    ...catalog.map((p) => ({ product_id: p.id, name: p.name, price: p.selling_price })),
    ...(demo?.products || []).filter((p) => !catalog.some((c) => c.id === p.product_id)),
  ];
  const compProductOptions = allProducts.filter((p) => !comp.products_demonstrated.some((x) => x.name === p.name));

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full overflow-y-auto scroll-slim p-0 sm:max-w-2xl">
        <SheetTitle className="sr-only">{demo?.number || "Demo"}</SheetTitle>
        <SheetDescription className="sr-only">Demo schedule, attendees, expenses and feedback.</SheetDescription>

        {loading || !demo ? (
          <div className="grid h-64 place-items-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : (
          <>
            <div className="bg-gradient-panel px-6 pb-5 pt-8 pr-14">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="num text-xs text-muted-foreground">{demo.number} &middot; {demo.kind}</p>
                  <h2 className="truncate font-display text-2xl font-bold">{demo.customer_name}</h2>
                  <p className="truncate text-sm text-muted-foreground">{demo.company_name}</p>
                </div>
                <StatusBadge status={demo.status} tone={TONE[demo.status]} />
              </div>
              <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1.5">
                  <CalendarClock className="h-3.5 w-3.5" />
                  {demo.start_date ? `${fdate(demo.start_date)} \u2013 ${fdate(demo.end_date)}`
                    : `proposed ${fdate(demo.proposed_start)}`}
                </span>
                <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" />{demo.actual_days || demo.days} day(s)</span>
                <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />{demo.mode}{demo.location ? ` \u00b7 ${demo.location}` : ""}</span>
                <span className="inline-flex items-center gap-1.5"><Users2 className="h-3.5 w-3.5" />{(demo.assigned_to_names || []).join(", ")}</span>
              </div>

              {editable && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {isOpenStatus && (
                    <>
                      <Button size="sm" variant="success" onClick={() => openAction("confirm")} data-testid="dd-confirm">
                        <CheckCircle2 className="h-3.5 w-3.5" /> Confirm dates
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => openAction("reschedule")} data-testid="dd-reschedule">
                        <CalendarClock className="h-3.5 w-3.5" /> Reschedule
                      </Button>
                      <Button size="sm" onClick={() => setMode("complete")} data-testid="dd-complete">
                        <CheckCircle2 className="h-3.5 w-3.5" /> Mark completed
                      </Button>
                      <Button size="sm" variant="ghost" className="text-destructive" onClick={() => openAction("cancel")}
                        data-testid="dd-cancel">
                        <Trash2 className="h-3.5 w-3.5" /> Cancel
                      </Button>
                    </>
                  )}
                  {isAdmin && (
                    <Button size="sm" variant="soft" data-testid="dd-admin-edit"
                      onClick={() => setAdminForm({
                        kind: demo.kind, status: demo.status, customer_name: demo.customer_name,
                        company_name: demo.company_name || "", contact_person: demo.contact_person || "",
                        contact_email: demo.contact_email || "", contact_phone: demo.contact_phone || "",
                        mode: demo.mode || "On-site", location: demo.location || "",
                        agenda: demo.agenda || "",
                        proposed_start: demo.proposed_start || "", proposed_end: demo.proposed_end || "",
                        start_date: demo.start_date || "", end_date: demo.end_date || "",
                        days: demo.days || 1, travel_start: demo.travel_start || "",
                        travel_end: demo.travel_end || "", actual_days: demo.actual_days || 0,
                        summary: demo.summary || "", performance: demo.performance || "Good",
                        outcome: demo.outcome || "Follow-up required",
                        attendees: demo.attendees?.length ? demo.attendees : [{ name: "", designation: "", email: "" }],
                        products: demo.products || [],
                        products_demonstrated: demo.products_demonstrated || [],
                        assigned_to_ids: demo.assigned_to_ids || [],
                        reason: "",
                      })}>
                      <Pencil className="h-3.5 w-3.5" /> Edit all details
                    </Button>
                  )}
                </div>
              )}
            </div>

            <Tabs defaultValue="overview">
              <div className="scroll-slim overflow-x-auto border-b border-border px-6">
                <TabsList className="h-11 bg-transparent p-0">
                  <TabsTrigger value="overview" data-testid="dd-tab-overview">Overview</TabsTrigger>
                  <TabsTrigger value="session" data-testid="dd-tab-session">Session</TabsTrigger>
                  <TabsTrigger value="expenses" data-testid="dd-tab-expenses">
                    Expenses{demo.expense_count ? ` (${demo.expense_count})` : ""}
                  </TabsTrigger>
                  <TabsTrigger value="timeline" data-testid="dd-tab-timeline">Timeline</TabsTrigger>
                </TabsList>
              </div>

              <TabsContent value="overview" className="m-0 px-6 py-5">
                <Row label="Contact" value={[demo.contact_person, demo.contact_phone, demo.contact_email].filter(Boolean).join(" \u00b7 ")} />
                <Row label="Proposed" value={`${fdate(demo.proposed_start)} \u2013 ${fdate(demo.proposed_end)}`} />
                <Row label="Confirmed" value={demo.start_date ? `${fdate(demo.start_date)} \u2013 ${fdate(demo.end_date)}` : "not confirmed yet"} />
                <Row label="Travel" value={demo.travel_start ? `${fdate(demo.travel_start)} \u2013 ${fdate(demo.travel_end)}` : ""} />
                <Row label="Products" value={(demo.products || []).map((p) => p.name).join(", ")} />
                <Row label="Agenda" value={demo.agenda} />
                <Row label="Reschedules" value={demo.reschedule_count ? `${demo.reschedule_count} \u00b7 ${demo.last_reschedule_reason || ""}` : "none"} />
                <Row label="Expenses" value={`${money(demo.expense_total || 0)} claimed \u00b7 ${money(demo.expense_approved || 0)} approved`} />
                <Row label="Created by" value={`${demo.created_by_name} \u00b7 ${fdatetime(demo.created_at)}`} />
              </TabsContent>

              <TabsContent value="session" className="m-0 space-y-4 px-6 py-5">
                {demo.status === "Proposed" || demo.status === "Confirmed" || demo.status === "Rescheduled" ? (
                  <p className="rounded-lg border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground">
                    Once the session is over, use <b>Mark completed</b> above to record the attendees,
                    the summary and to ask the customer for a rating.
                  </p>
                ) : (
                  <>
                    <Row label="Performance" value={demo.performance} />
                    <Row label="Outcome" value={demo.outcome} />
                    <Row label="Actual duration" value={`${demo.actual_days} day(s)`} />
                    <Row label="Completed" value={`${demo.completed_by_name || ""} \u00b7 ${fdatetime(demo.completed_at)}`} />
                    <div data-testid="dd-products-demonstrated">
                      <p className="mb-2 text-xs uppercase tracking-wider text-muted-foreground">Products actually demonstrated</p>
                      <div className="flex flex-wrap gap-2">
                        {(demo.products_demonstrated || []).map((p) => (
                          <span key={p.product_id || p.name}
                            className="rounded-full border border-primary/40 bg-primary-soft px-2.5 py-1 text-xs font-medium">
                            {p.name}
                          </span>
                        ))}
                        {(demo.products_demonstrated || []).length === 0 && (
                          <span className="text-sm text-muted-foreground">Not recorded.</span>
                        )}
                      </div>
                    </div>
                    <div>
                      <p className="mb-2 text-xs uppercase tracking-wider text-muted-foreground">Attendees</p>
                      <div className="space-y-1.5">
                        {(demo.attendees || []).map((a, i) => (
                          <p key={i} className="rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm">
                            <b>{a.name}</b>{a.designation ? ` \u00b7 ${a.designation}` : ""}
                            {a.email ? <span className="text-muted-foreground"> \u00b7 {a.email}</span> : null}
                          </p>
                        ))}
                      </div>
                    </div>
                    <div>
                      <p className="mb-1.5 text-xs uppercase tracking-wider text-muted-foreground">Summary</p>
                      <p className="whitespace-pre-wrap rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm">{demo.summary}</p>
                    </div>
                    <div className="rounded-xl border border-primary/30 bg-primary-soft/40 p-4" data-testid="dd-feedback">
                      <p className="text-xs uppercase tracking-wider text-muted-foreground">Customer feedback</p>
                      {demo.feedback ? (
                        <div className="mt-2 space-y-1.5">
                          <div className="flex items-center gap-2">
                            <Stars value={demo.feedback.rating} />
                            <span className="num text-sm font-bold">{demo.feedback.rating}/5</span>
                          </div>
                          {demo.feedback.comments && <p className="text-sm italic">&ldquo;{demo.feedback.comments}&rdquo;</p>}
                          <p className="text-[11px] text-muted-foreground">
                            Received {fdatetime(demo.feedback.submitted_at)}
                            {demo.feedback.would_recommend ? " \u00b7 would recommend us" : ""}
                          </p>
                        </div>
                      ) : (
                        <p className="mt-1 text-sm text-muted-foreground">
                          A rating link has been emailed to {demo.contact_email || "the customer"}. Their
                          reply lands here automatically.
                        </p>
                      )}
                    </div>
                  </>
                )}
              </TabsContent>

              <TabsContent value="expenses" className="m-0 px-6 py-5">
                <DemoExpenses demo={demo} onChanged={() => { load(true); onChanged?.(); }} />
              </TabsContent>

              <TabsContent value="timeline" className="m-0 px-6 py-5">
                <div className="space-y-3">
                  {(demo.history || []).slice().reverse().map((h, i) => (
                    <div key={i} className="relative pl-5">
                      <span className="absolute left-0 top-1.5 h-2 w-2 rounded-full bg-primary" />
                      <p className="text-sm font-semibold">{h.action}</p>
                      {h.note && <p className="text-xs text-muted-foreground">{h.note}</p>}
                      <p className="text-[11px] text-muted-foreground">{h.by} &middot; {fdatetime(h.at)}</p>
                    </div>
                  ))}
                </div>
              </TabsContent>
            </Tabs>
          </>
        )}

        {/* confirm / reschedule / cancel */}
        <Dialog open={!!mode && mode !== "complete"} onOpenChange={(v) => !v && setMode("")}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-display text-xl">
                {mode === "confirm" ? "Confirm the dates" : mode === "reschedule" ? "Reschedule" : "Cancel this visit"}
              </DialogTitle>
              <DialogDescription>
                {mode === "confirm" && "The customer receives a confirmation email with these dates."}
                {mode === "reschedule" && "The customer and the directors are both told about the change."}
                {mode === "cancel" && "The team is notified. Nothing is emailed to the customer."}
              </DialogDescription>
            </DialogHeader>

            {mode === "cancel" ? (
              <Field label="Reason">
                <Textarea rows={2} value={form.remarks || ""} data-testid="dd-cancel-reason"
                  onChange={(e) => setForm({ ...form, remarks: e.target.value })} />
              </Field>
            ) : (
              <div className="space-y-4">
                <div className="grid gap-3 sm:grid-cols-3">
                  <Field label="Start date" required>
                    <Input type="date" value={form.start_date || ""} data-testid="dd-start"
                      onChange={(e) => setActStart(e.target.value)} />
                  </Field>
                  <Field label="End date">
                    <Input type="date" value={form.end_date || ""} data-testid="dd-end"
                      min={form.start_date} onChange={(e) => setActEnd(e.target.value)} />
                  </Field>
                  <Field label="Days" hint="From the dates.">
                    <Input type="number" min="0.5" step="0.5" value={form.days || 1} data-testid="dd-days"
                      onChange={(e) => setActDays(e.target.value)} />
                  </Field>
                  <Field label="Travel starts">
                    <Input type="date" value={form.travel_start || ""} data-testid="dd-travel-start"
                      onChange={(e) => { setTravelEdited(true); setForm({ ...form, travel_start: e.target.value }); }} />
                  </Field>
                  <Field label="Travel ends">
                    <Input type="date" value={form.travel_end || ""} data-testid="dd-travel-end"
                      onChange={(e) => { setTravelEdited(true); setForm({ ...form, travel_end: e.target.value }); }} />
                  </Field>
                </div>
                {mode === "reschedule" ? (
                  <Field label="Why are the dates changing?" required hint="This exact reason goes into the email.">
                    <Textarea rows={2} value={form.reason || ""} data-testid="dd-reason"
                      onChange={(e) => setForm({ ...form, reason: e.target.value })} />
                  </Field>
                ) : (
                  <Field label="Note for the customer">
                    <Textarea rows={2} value={form.note || ""} data-testid="dd-note"
                      onChange={(e) => setForm({ ...form, note: e.target.value })} />
                  </Field>
                )}
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setMode("")} data-testid="dd-action-back">Back</Button>
              <Button onClick={runAction} disabled={busy} data-testid="dd-action-save">
                {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                {mode === "confirm" ? "Confirm and email" : mode === "reschedule" ? "Reschedule and email" : "Cancel visit"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* completion */}
        {demo && <Dialog open={mode === "complete"} onOpenChange={(v) => !v && setMode("")}>
          <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto scroll-slim">
            <DialogHeader>
              <DialogTitle className="font-display text-xl">Close the session</DialogTitle>
              <DialogDescription>
                Record who attended and how it went. The customer is then emailed a one-tap rating link.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Attendees</p>
                {comp.attendees.map((a, i) => (
                  <div key={i} className="grid gap-2 sm:grid-cols-12">
                    <Input className="sm:col-span-5" placeholder="Name" value={a.name} data-testid={`att-name-${i}`}
                      onChange={(e) => setComp({
                        ...comp, attendees: comp.attendees.map((x, j) => j === i ? { ...x, name: e.target.value } : x),
                      })} />
                    <Input className="sm:col-span-4" placeholder="Designation" value={a.designation} data-testid={`att-desig-${i}`}
                      onChange={(e) => setComp({
                        ...comp, attendees: comp.attendees.map((x, j) => j === i ? { ...x, designation: e.target.value } : x),
                      })} />
                    <Input className="sm:col-span-3" placeholder="Email" value={a.email} data-testid={`att-email-${i}`}
                      onChange={(e) => setComp({
                        ...comp, attendees: comp.attendees.map((x, j) => j === i ? { ...x, email: e.target.value } : x),
                      })} />
                  </div>
                ))}
                <Button type="button" size="sm" variant="soft" data-testid="att-add"
                  onClick={() => setComp({ ...comp, attendees: [...comp.attendees, { name: "", designation: "", email: "" }] })}>
                  <Plus className="h-3.5 w-3.5" /> Add attendee
                </Button>
              </div>

              <div className="grid gap-3 sm:grid-cols-3">
                <Field label="Days actually spent">
                  <Input type="number" min="0.5" step="0.5" value={comp.actual_days} data-testid="comp-days"
                    onChange={(e) => setComp({ ...comp, actual_days: e.target.value })} />
                </Field>
                <Field label="Travel actually started">
                  <Input type="date" value={comp.travel_start} data-testid="comp-travel-start"
                    onChange={(e) => setComp({ ...comp, travel_start: e.target.value })} />
                </Field>
                <Field label="Travel actually ended">
                  <Input type="date" value={comp.travel_end} data-testid="comp-travel-end"
                    onChange={(e) => setComp({ ...comp, travel_end: e.target.value })} />
                </Field>
                <Field label="How did it go?">
                  <Select value={comp.performance} onValueChange={(v) => setComp({ ...comp, performance: v })}>
                    <SelectTrigger data-testid="comp-performance"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["Excellent", "Good", "Average", "Poor"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Next step">
                  <Select value={comp.outcome} onValueChange={(v) => setComp({ ...comp, outcome: v })}>
                    <SelectTrigger data-testid="comp-outcome"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["Converted", "Needs another demo", "Follow-up required", "Not interested"]
                        .map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
              </div>

              <Field label="Products actually demonstrated" hint="Prefilled from the plan - add anything else from the product list or remove what was not shown.">
                <div className="space-y-2" data-testid="comp-products">
                  <div className="flex flex-wrap gap-2">
                    {comp.products_demonstrated.map((p) => (
                      <span key={p.product_id || p.name} className="flex items-center gap-1.5 rounded-full border border-primary/40 bg-primary-soft px-2.5 py-1 text-xs font-medium">
                        {p.name}
                        <button type="button" data-testid={`comp-product-remove-${p.name}`} onClick={() => setComp({
                          ...comp, products_demonstrated: comp.products_demonstrated.filter((x) => x.name !== p.name),
                        })}><XCircle className="h-3 w-3" /></button>
                      </span>
                    ))}
                    {comp.products_demonstrated.length === 0 && (
                      <span className="text-xs text-muted-foreground">Nothing selected.</span>
                    )}
                  </div>
                  <Select value="" onValueChange={(id) => {
                    const c = catalog.find((x) => x.id === id);
                    const p = (demo.products || []).find((x) => x.product_id === id)
                      || (c && { product_id: c.id, name: c.name, price: c.selling_price })
                      || { product_id: id, name: id };
                    if (!comp.products_demonstrated.some((x) => x.name === p.name)) {
                      setComp({ ...comp, products_demonstrated: [...comp.products_demonstrated, p] });
                    }
                  }}>
                    <SelectTrigger data-testid="comp-add-product"><SelectValue placeholder="Add a product" /></SelectTrigger>
                    <SelectContent>
                      {compProductOptions.map((p) => (
                        <SelectItem key={p.product_id} value={p.product_id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </Field>

              <Field label="Summary of the session" required>
                <Textarea rows={3} value={comp.summary} data-testid="comp-summary"
                  onChange={(e) => setComp({ ...comp, summary: e.target.value })}
                  placeholder="Trained 6 operators, covered changeover and daily checks, two open points on tooling." />
              </Field>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setMode("")} data-testid="comp-back">Back</Button>
              <Button onClick={complete} disabled={busy} data-testid="comp-save">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                Close and ask for feedback
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        }


        {/* admin: edit everything, even after closing */}
        <Dialog open={!!adminForm} onOpenChange={(v) => !v && setAdminForm(null)}>
          <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto scroll-slim">
            <DialogHeader>
              <DialogTitle className="font-display text-xl">Edit every detail</DialogTitle>
              <DialogDescription>
                Administrator override. Nothing is emailed to the customer, but every change is written
                to the timeline with your name against it.
              </DialogDescription>
            </DialogHeader>

            {adminForm && (
              <div className="space-y-4">
                <div className="grid gap-3 sm:grid-cols-2">
                  <Field label="Type">
                    <Select value={adminForm.kind} onValueChange={(v) => setAdminForm({ ...adminForm, kind: v })}>
                      <SelectTrigger data-testid="ae-kind"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Demo">Demo</SelectItem>
                        <SelectItem value="Training">Training</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Status">
                    <Select value={adminForm.status} onValueChange={(v) => setAdminForm({ ...adminForm, status: v })}>
                      <SelectTrigger data-testid="ae-status"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {["Proposed", "Confirmed", "Rescheduled", "Completed", "Feedback Received", "Cancelled"]
                          .map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Customer name">
                    <Input value={adminForm.customer_name} data-testid="ae-customer"
                      onChange={(e) => setAdminForm({ ...adminForm, customer_name: e.target.value })} />
                  </Field>
                  <Field label="Company">
                    <Input value={adminForm.company_name} data-testid="ae-company"
                      onChange={(e) => setAdminForm({ ...adminForm, company_name: e.target.value })} />
                  </Field>
                  <Field label="Contact person">
                    <Input value={adminForm.contact_person} data-testid="ae-contact-person"
                      onChange={(e) => setAdminForm({ ...adminForm, contact_person: e.target.value })} />
                  </Field>
                  <Field label="Contact email">
                    <Input value={adminForm.contact_email} data-testid="ae-contact-email"
                      onChange={(e) => setAdminForm({ ...adminForm, contact_email: e.target.value })} />
                  </Field>
                  <Field label="Contact phone">
                    <Input value={adminForm.contact_phone} data-testid="ae-contact-phone"
                      onChange={(e) => setAdminForm({ ...adminForm, contact_phone: e.target.value })} />
                  </Field>
                  <Field label="Venue">
                    <Input value={adminForm.location} data-testid="ae-location"
                      onChange={(e) => setAdminForm({ ...adminForm, location: e.target.value })} />
                  </Field>
                  <Field label="Mode">
                    <Select value={adminForm.mode} onValueChange={(v) => setAdminForm({ ...adminForm, mode: v })}>
                      <SelectTrigger data-testid="ae-mode"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {["On-site", "Online", "At our office"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>

                <div className="grid gap-3 sm:grid-cols-3">
                  {[["start_date", "Start date"], ["end_date", "End date"],
                    ["travel_start", "Travel start"], ["travel_end", "Travel end"],
                    ["proposed_start", "Proposed start"], ["proposed_end", "Proposed end"]].map(([k, l]) => (
                    <Field key={k} label={l}>
                      <Input type="date" value={adminForm[k] || ""} data-testid={`ae-${k}`}
                        onChange={(e) => setAdminForm({ ...adminForm, [k]: e.target.value })} />
                    </Field>
                  ))}
                  <Field label="Planned days">
                    <Input type="number" min="0" step="0.5" value={adminForm.days} data-testid="ae-days"
                      onChange={(e) => setAdminForm({ ...adminForm, days: e.target.value })} />
                  </Field>
                  <Field label="Actual days">
                    <Input type="number" min="0" step="0.5" value={adminForm.actual_days} data-testid="ae-actual-days"
                      onChange={(e) => setAdminForm({ ...adminForm, actual_days: e.target.value })} />
                  </Field>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <Field label="Performance">
                    <Select value={adminForm.performance} onValueChange={(v) => setAdminForm({ ...adminForm, performance: v })}>
                      <SelectTrigger data-testid="ae-performance"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {["Excellent", "Good", "Average", "Poor"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Outcome">
                    <Select value={adminForm.outcome} onValueChange={(v) => setAdminForm({ ...adminForm, outcome: v })}>
                      <SelectTrigger data-testid="ae-outcome"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {["Converted", "Needs another demo", "Follow-up required", "Not interested"]
                          .map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>

                <div className="space-y-2">
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Attendees</p>
                  {(adminForm.attendees || []).map((a, i) => (
                    <div key={i} className="grid gap-2 sm:grid-cols-12">
                      <Input className="sm:col-span-5" placeholder="Name" value={a.name || ""} data-testid={`ae-att-${i}`}
                        onChange={(e) => setAdminForm({
                          ...adminForm, attendees: adminForm.attendees.map((x, j) => j === i ? { ...x, name: e.target.value } : x),
                        })} />
                      <Input className="sm:col-span-4" placeholder="Designation" value={a.designation || ""} data-testid={`ae-att-desig-${i}`}
                        onChange={(e) => setAdminForm({
                          ...adminForm, attendees: adminForm.attendees.map((x, j) => j === i ? { ...x, designation: e.target.value } : x),
                        })} />
                      <Input className="sm:col-span-3" placeholder="Email" value={a.email || ""} data-testid={`ae-att-email-${i}`}
                        onChange={(e) => setAdminForm({
                          ...adminForm, attendees: adminForm.attendees.map((x, j) => j === i ? { ...x, email: e.target.value } : x),
                        })} />
                    </div>
                  ))}
                  <Button type="button" size="sm" variant="soft" data-testid="ae-att-add"
                    onClick={() => setAdminForm({ ...adminForm, attendees: [...(adminForm.attendees || []), { name: "", designation: "", email: "" }] })}>
                    <Plus className="h-3.5 w-3.5" /> Add attendee
                  </Button>
                </div>

                <Field label="Summary">
                  <Textarea rows={3} value={adminForm.summary} data-testid="ae-summary"
                    onChange={(e) => setAdminForm({ ...adminForm, summary: e.target.value })} />
                </Field>
                <ProductPicker
                  label="Planned products" testId="ae-products" options={allProducts}
                  picked={adminForm.products || []}
                  onChange={(products) => setAdminForm({ ...adminForm, products })} />
                <ProductPicker
                  label="Products actually demonstrated" testId="ae-products-demonstrated" options={allProducts}
                  picked={adminForm.products_demonstrated || []}
                  onChange={(products_demonstrated) => setAdminForm({ ...adminForm, products_demonstrated })} />
                <Field label="Team assigned" hint="Tap a name to add or remove.">
                  <div className="flex flex-wrap gap-2" data-testid="ae-team">
                    {staff.map((s) => {
                      const on = (adminForm.assigned_to_ids || []).includes(s.id);
                      return (
                        <button key={s.id} type="button" data-testid={`ae-team-${s.id}`}
                          onClick={() => setAdminForm({
                            ...adminForm,
                            assigned_to_ids: on
                              ? adminForm.assigned_to_ids.filter((x) => x !== s.id)
                              : [...(adminForm.assigned_to_ids || []), s.id],
                          })}
                          className={cn("rounded-full border px-2.5 py-1 text-xs font-medium transition-colors",
                            on ? "border-primary/40 bg-primary-soft text-primary" : "border-border text-muted-foreground hover:border-primary/30")}>
                          {s.name}
                        </button>
                      );
                    })}
                  </div>
                </Field>
                <Field label="Agenda">
                  <Textarea rows={2} value={adminForm.agenda} data-testid="ae-agenda"
                    onChange={(e) => setAdminForm({ ...adminForm, agenda: e.target.value })} />
                </Field>
                <Field label="Why are you changing this?" hint="Shown on the timeline next to the change.">
                  <Input value={adminForm.reason} data-testid="ae-reason"
                    onChange={(e) => setAdminForm({ ...adminForm, reason: e.target.value })} />
                </Field>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setAdminForm(null)} data-testid="ae-cancel">Cancel</Button>
              <Button onClick={saveAdmin} disabled={busy} data-testid="ae-save">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Pencil className="h-4 w-4" />}
                Save corrections
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </SheetContent>
    </Sheet>
  );
};
